import { Position } from 'types'
import { MediaSprite } from '../../../types'
import { ArtboardLayerRef } from '../types/refs'

type Props = {
    crop?: Position
    duration: number
    fps: number
    excludeLayerIndexes?: number[]
    height: number
    width: number
}

export function getMergedFrame(layers: ArtboardLayerRef[], frame: number, props: Props): HTMLCanvasElement {
    const { crop, excludeLayerIndexes = [] } = props
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    const offsetLeft = crop?.x || 0
    const offsetTop = crop?.y || 0

    canvas.width = crop ? crop.width : props.width
    canvas.height = crop ? crop.height : props.height

    if (context) {
        layers
            .filter((_, i) => excludeLayerIndexes.indexOf(i) < 0)
            .forEach((layer) => {
                const { canvas: spriteCanvas, frameHeight, frameScale = 1, frameWidth } = layer.getSprite()
                const { x, y, rotation = 0, width, height } = layer.getPosition()
                const frameCoordinates = layer.getSpriteFrameCoordinates(frame)
                const isMirrored = layer.getMirror()

                if (frameCoordinates) {
                    context.save()
                    context.translate(x, y)
                    context.scale(isMirrored ? -1 : 1, 1)
                    context.rotate(rotation * (Math.PI / 180))
                    context.drawImage(
                        spriteCanvas,
                        frameCoordinates.x * frameScale,
                        frameCoordinates.y * frameScale,
                        frameWidth * frameScale,
                        frameHeight * frameScale,
                        -width / 2 - offsetLeft,
                        -height / 2 - offsetTop,
                        width,
                        height
                    )
                    context.restore()
                }
            })
    }

    return canvas
}

export function getMergedFrames(layers: ArtboardLayerRef[], count: number, props: Props): HTMLCanvasElement[] {
    const { duration, fps } = props
    const durationInFrames = Math.round(duration * fps)
    const canvases: HTMLCanvasElement[] = []

    for (let i = 0; i < count; i++) {
        const frame = Math.floor(durationInFrames * (i / count))
        const canvas = getMergedFrame(layers, frame, props)
        canvases.push(canvas)
    }

    return canvases
}

export function getMergedSprite(layers: ArtboardLayerRef[], count: number, props: Props): MediaSprite {
    const { crop, duration, height, width } = props
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    const frames = getMergedFrames(layers, count, props)
    const delay = Math.round((duration / frames.length) * 1000)
    const columns = Math.round(Math.sqrt(frames.length))
    const rows = Math.ceil(frames.length / columns)
    const frameIndexes: number[] = []

    const frameWidth = crop ? crop.width : width
    const frameHeight = crop ? crop.height : height

    canvas.width = Math.round(frameWidth * columns)
    canvas.height = Math.round(frameHeight * rows)

    frames.forEach((frame, i) => {
        const x = (i % columns) * frameWidth
        const y = Math.floor(i / columns) * frameHeight

        context?.drawImage(frame, x, y, frameWidth, frameHeight)
        frameIndexes.push(i)
    })

    return {
        canvas,
        columns,
        duration: delay * frames.length,
        frameHeight,
        frameIndexes,
        frameWidth,
        numFrames: frames.length,
        rows,
    }
}
